/**
 * @fileoverview Lookup filtering, normalization, and module-membership helpers.
 * @module core-domain/forms/lookupFiltering
 *
 * Responsibilities:
 * - Normalize entity-category and inspection-module selections on lookup values
 *   and filter contexts to canonical or catalog-driven codes.
 * - Filter and dedupe lookup values by activity, search, scope policy,
 *   entity category, and inspection module.
 * - Collect explicit module membership without treating untagged values as members.
 * - Resolve display strings and inspection-type options for tenant-web and mobile-pwa.
 *
 * Dependencies: None — pure functions.
 * Architecture: Shared domain layer consumed by tenant-web and mobile-pwa lookup services.
 *   Catalog module codes come from the MODULE registry (lowerCamelCase,
 *   name-derived); lookup `inspectionModules` keys are a separate persisted
 *   vocabulary (`fireCode`, `communityRisk`, `construction`, …).
 * Notes: Ordinary dropdown filtering keeps unrestricted/untagged values. An explicit
 *   empty module context is fail-closed to untagged values, while absent module
 *   context remains unrestricted.
 */
export const LOOKUP_ENTITY_CATEGORIES = [
  'parcel',
  'building',
  'hydrant',
  'site',
  'complex',
  'residential',
] as const;

export type LookupEntityCategory = (typeof LOOKUP_ENTITY_CATEGORIES)[number];

/** Catalog-driven lookup module key; the system modules catalog remains authoritative. */
export type LookupInspectionModuleKey = string;

export type LookupScopeMode = 'all' | 'entity' | 'module' | 'entityAndModule';

export interface LookupScopePolicy {
  scopeMode: LookupScopeMode;
  enforceCategory: boolean;
  enforceModule: boolean;
}

import { MODULE } from '../modules/moduleRegistry';

export const DEFAULT_LOOKUP_SCOPE_POLICY: LookupScopePolicy = {
  scopeMode: 'all',
  enforceCategory: false,
  enforceModule: false,
};

export interface LookupFilterContext {
  appliesTo?: string | string[];
  entityCategory?: string | null;
  entityCategories?: Array<string | null | undefined>;
  inspectionModule?: string | null;
  inspectionModules?: Array<string | null | undefined>;
  includeInactive?: boolean;
  search?: string;
  dedupeByCode?: boolean;
  availableAppliesTo?: string[];
  scopePolicy?: LookupScopePolicy | null;
}

export interface LookupFilterableValue {
  id?: string;
  lookupType: string;
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
  _deleted?: boolean;
  appliesTo?: string[];
  category?: string | string[];
  inspectionModules?: string[];
  requiredModules?: string[];
}

const LOOKUP_ENTITY_CATEGORY_SET = new Set<string>(LOOKUP_ENTITY_CATEGORIES);

// Legacy/variant spellings canonicalized onto the persisted lookup module key
// (the same keys MODULE_CATALOG_TO_LOOKUP_KEYS emits), so every spelling of a
// module matches on both the value and the context side. Codes with no alias
// pass through verbatim — unknown-but-real catalog codes (e.g. `permits`) must survive.
const MODULE_ALIASES: Record<string, string> = {
  wildland: 'wildland',
  wildlandfire: 'wildland',
  'wildland fire': 'wildland',
  'wildland inspections': 'wildland',
  'wild land': 'wildland',
  firecode: 'fireCode',
  firecodecompliance: 'fireCode',
  'fire code compliance': 'fireCode',
  'fire-code': 'fireCode',
  'fire code': 'fireCode',
  'fire-code-compliance': 'fireCode',
  building: 'fireCode',
  hydrant: 'hydrant',
  hydrantmanagement: 'hydrant',
  'hydrant management': 'hydrant',
  'hydrant-management': 'hydrant',
  construction: 'construction',
  buildingcodecompliance: 'construction',
  'community risk': 'communityRisk',
  communityrisk: 'communityRisk',
  'community-risk': 'communityRisk',
  'community-risk-reduction': 'communityRisk',
  community_risk: 'communityRisk',
  'community risk reduction': 'communityRisk',
  communityriskreduction: 'communityRisk',
  crr: 'communityRisk',
  'building code compliance': 'construction',
  'permits & certificates': 'permits',
  permitscertificates: 'permits',
  permits: 'permits',
};

function normalizeString(value: string | null | undefined): string {
  return String(value ?? '').trim();
}

function normalizeStringArray(values: Array<string | null | undefined> | string | null | undefined): string[] {
  const source = Array.isArray(values) ? values : [values];
  return Array.from(new Set(source.map((value) => normalizeString(value)).filter(Boolean)));
}

export function normalizeLookupEntityCategory(value: string | null | undefined): LookupEntityCategory | undefined {
  const normalized = normalizeString(value).toLowerCase();
  return LOOKUP_ENTITY_CATEGORY_SET.has(normalized) ? (normalized as LookupEntityCategory) : undefined;
}

export function normalizeLookupInspectionModuleKey(value: string | null | undefined): string | undefined {
  const normalized = normalizeString(value).toLowerCase();
  if (!normalized) return undefined;
  return MODULE_ALIASES[normalized] ?? normalized;
}

export function normalizeLookupEntityCategories(values: Array<string | null | undefined> | string | null | undefined): LookupEntityCategory[] {
  const result: LookupEntityCategory[] = [];
  for (const value of normalizeStringArray(values)) {
    const normalized = normalizeLookupEntityCategory(value);
    if (normalized && !result.includes(normalized)) {
      result.push(normalized);
    }
  }
  return result;
}

export function normalizeLookupInspectionModules(values: Array<string | null | undefined> | string | null | undefined): string[] {
  const result: string[] = [];
  for (const value of normalizeStringArray(values)) {
    const normalized = normalizeLookupInspectionModuleKey(value);
    if (normalized && !result.includes(normalized)) {
      result.push(normalized);
    }
  }
  return result;
}

/**
 * Catalog module code → lookup `inspectionModules` keys.
 * Distinct from MODULE_TEMPLATE_TYPES: this drives persisted lookup tags.
 */
export const MODULE_CATALOG_TO_LOOKUP_KEYS: Record<string, LookupInspectionModuleKey[]> = {
  [MODULE.WILDLAND_FIRE]: ['wildland'],
  [MODULE.FIRE_CODE_COMPLIANCE]: ['fireCode'],
  [MODULE.BUILDING_CODE_COMPLIANCE]: ['construction'],
  [MODULE.HYDRANT_MANAGEMENT]: ['hydrant'],
  [MODULE.COMMUNITY_RISK_REDUCTION]: ['communityRisk'],
  [MODULE.PERMITS_CERTIFICATES]: [],
};

export function getModuleLookupModuleKeys(moduleCode: string): LookupInspectionModuleKey[] {
  return MODULE_CATALOG_TO_LOOKUP_KEYS[moduleCode] ?? [];
}

/**
 * @process resolveLookupInspectionModulesForCatalog
 *
 * Steps:
 * 1. Treat undefined catalog codes as "no module filter".
 * 2. Map each catalog code to lookup keys, then fall back to aliases.
 * 3. Deduplicate keys.
 *
 * Inputs: tenant-enabled catalog module codes (`wildland`, `fire-code`, …).
 * Outputs: lookup inspection-module keys, or undefined when the catalog set is unknown.
 * Failure Handling: unknown catalog codes contribute no keys.
 * Idempotency: REQUIRED.
 */
export function resolveLookupInspectionModulesForCatalog(
  catalogCodes: Array<string | null | undefined> | undefined,
): LookupInspectionModuleKey[] | undefined {
  if (catalogCodes === undefined) return undefined;
  const keys: LookupInspectionModuleKey[] = [];
  for (const code of catalogCodes) {
    const trimmed = normalizeString(code);
    if (!trimmed) continue;
    const mapped = getModuleLookupModuleKeys(trimmed);
    const resolved = mapped.length > 0 ? mapped : normalizeLookupInspectionModules([trimmed]);
    for (const key of resolved) {
      if (!keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

export function getLookupValueEntityCategories(value: Pick<LookupFilterableValue, 'category'>): LookupEntityCategory[] {
  return normalizeLookupEntityCategories(value.category);
}

export function getLookupValueInspectionModules(
  value: Pick<LookupFilterableValue, 'inspectionModules' | 'requiredModules'>,
): string[] {
  return normalizeLookupInspectionModules(value.inspectionModules ?? value.requiredModules);
}

/**
 * Collects lookup codes whose `inspectionModules` metadata explicitly includes
 * one of the given module keys.
 *
 * Untagged values (empty inspectionModules) are excluded. They remain globally
 * applicable for ordinary dropdown filtering via `filterLookupValues`, but they
 * must not become members of every module-specific view.
 */
export function collectExplicitModuleLookupCodes<T extends Pick<LookupFilterableValue, 'code' | 'lookupType' | 'isActive' | '_deleted' | 'inspectionModules' | 'requiredModules'>>(
  values: T[],
  moduleKeys: Array<string | null | undefined>,
  lookupType = 'caseType',
): string[] {
  const selections = new Set(normalizeLookupInspectionModules(moduleKeys));
  if (selections.size === 0) {
    return [];
  }

  const codes: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    if (value.lookupType !== lookupType) continue;
    if (value._deleted === true || value.isActive === false) continue;
    const modules = getLookupValueInspectionModules(value);
    if (modules.length === 0) continue;
    if (!modules.some((module) => selections.has(module))) continue;
    if (seen.has(value.code)) continue;
    seen.add(value.code);
    codes.push(value.code);
  }
  return codes;
}

function isUnrestricted(selectedValues: string[], universe: readonly string[]): boolean {
  if (selectedValues.length === 0) {
    return true;
  }
  if (universe.length === 0) {
    return false;
  }
  return universe.every((value) => selectedValues.includes(value));
}

function matchesRestriction(
  valueSelections: string[],
  contextSelections: string[],
  universe: readonly string[] = [],
): boolean {
  if (contextSelections.length === 0) {
    return true;
  }
  if (isUnrestricted(valueSelections, universe)) {
    return true;
  }
  return contextSelections.some((selection) => valueSelections.includes(selection));
}

function matchesAppliesTo(valueSelections: string[], context: LookupFilterContext): boolean {
  const policy = context.scopePolicy ?? DEFAULT_LOOKUP_SCOPE_POLICY;
  if (policy.scopeMode === 'all') {
    return true;
  }
  const contextSelections = normalizeStringArray(context.appliesTo);
  if (contextSelections.length === 0) {
    return true;
  }
  if (isUnrestricted(valueSelections, normalizeStringArray(context.availableAppliesTo))) {
    return true;
  }
  return contextSelections.some((selection) => valueSelections.includes(selection));
}

function matchesSearch(value: Pick<LookupFilterableValue, 'code' | 'name' | 'description'>, search: string | undefined): boolean {
  const term = normalizeString(search).toLowerCase();
  if (!term) {
    return true;
  }
  return [value.code, value.name, value.description]
    .map((entry) => normalizeString(entry).toLowerCase())
    .some((entry) => entry.includes(term));
}

export function filterLookupValues<T extends LookupFilterableValue>(values: T[], context: LookupFilterContext = {}): T[] {
  const entitySelections = normalizeLookupEntityCategories(context.entityCategories ?? context.entityCategory);
  const moduleSelections = normalizeLookupInspectionModules(context.inspectionModules ?? context.inspectionModule);
  // An explicitly PROVIDED (but empty) module context means "no enabled
  // modules" — restrict to untagged values instead of opening everything.
  // Only an ABSENT context is unrestricted, so a tenant whose module
  // projection has not resolved can't flash disabled-module types.
  const moduleContextProvided =
    context.inspectionModules !== undefined || context.inspectionModule !== undefined;
  const matchesModuleRestriction = (value: T): boolean => {
    const valueSelections = getLookupValueInspectionModules(value);
    if (moduleSelections.length > 0) {
      return matchesRestriction(valueSelections, moduleSelections);
    }
    return moduleContextProvided ? valueSelections.length === 0 : true;
  };

  const filtered = values
    .filter((value) => context.includeInactive || (value._deleted !== true && value.isActive !== false))
    .filter((value) => matchesSearch(value, context.search))
    .filter((value) => matchesAppliesTo(normalizeStringArray(value.appliesTo), context))
    .filter((value) => matchesRestriction(getLookupValueEntityCategories(value), entitySelections, LOOKUP_ENTITY_CATEGORIES))
    .filter(matchesModuleRestriction)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));

  if (context.dedupeByCode === false) {
    return filtered;
  }

  const seen = new Set<string>();
  const deduped: T[] = [];
  for (const value of filtered) {
    if (seen.has(value.code)) {
      continue;
    }
    seen.add(value.code);
    deduped.push(value);
  }

  return deduped;
}

/**
 * @contract uniqueCaseTypeOptions
 * Purpose: Produce a unique Case Type dropdown list for tenant-web and mobile.
 * Fields:  code, name
 * Rules:   First distinct code wins; duplicate display names are also dropped.
 * Sync:    Read-only helper; does not rewrite lookup documents.
 */
export function uniqueCaseTypeOptions<T extends { code: string; name: string }>(options: T[]): T[] {
  const seenCodes = new Set<string>();
  const seenNames = new Set<string>();
  const unique: T[] = [];
  for (const option of options) {
    const code = normalizeString(option.code).toLowerCase();
    const name = normalizeString(option.name).toLowerCase();
    if (!code || seenCodes.has(code) || (name && seenNames.has(name))) continue;
    seenCodes.add(code);
    if (name) seenNames.add(name);
    unique.push(option);
  }
  return unique;
}

/**
 * @contract preferInspectionTypesForCase
 * Purpose: Prefer inspection types linked to the selected Case Type without
 *          emptying the Add Case dropdown when no tags match.
 * Fields:  code, name, optional caseTypeCodes
 * Rules:   Unique by code/name. Untagged types stay visible. If nothing matches
 *          the selected case type, return the full unique catalog.
 * Sync:    Read-only helper; does not rewrite lookup documents.
 */
export function preferInspectionTypesForCase<T extends { code: string; name: string; caseTypeCodes?: string[] }>(
  options: T[],
  caseTypeCode: string | null | undefined,
): T[] {
  const unique = uniqueCaseTypeOptions(options);
  const selected = normalizeString(caseTypeCode).toLowerCase();
  if (!selected) return unique;
  const taggedForCase = unique.filter((option) => {
    const codes = Array.isArray(option.caseTypeCodes) ? option.caseTypeCodes : [];
    return codes.some((code) => normalizeString(code).toLowerCase() === selected);
  });
  if (taggedForCase.length === 0) return unique;
  return unique.filter((option) => {
    const codes = Array.isArray(option.caseTypeCodes) ? option.caseTypeCodes : [];
    return codes.length === 0 || taggedForCase.includes(option);
  });
}

/**
 * Resolves a display string from a lookup object or plain string.
 * @param val The value to resolve (LookupReference-like object or string).
 * @param preferCode Set to true when the value is used for logic/validation (e.g. phone types),
 *                   false when used for UI display (e.g. roles).
 */
/**
 * Resolves a display string from a lookup object or plain string.
 * @param val The value to resolve (LookupReference-like object or string).
 * @param preferCode Set to true when the value is used for logic/validation (e.g. phone types),
 *                   false when used for UI display (e.g. roles).
 */
export function resolveLookupDisplayValue(val: unknown, preferCode = false): string {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
    const record = val as Record<string, unknown>;
    if (preferCode) {
      // Logic/validation path: strictly prefer the machine-readable code
      const code = record.code;
      return (typeof code === 'string' || typeof code === 'number') ? String(code) : '';
    }
    // UI display path: prefer the friendly name/label
    const candidate = record.name ?? record.label ?? record.code;
    return (candidate != null && candidate !== '') ? String(candidate) : '';
  }
  return (typeof val === 'number' || typeof val === 'boolean') ? String(val) : '';
}

export function findLookupValue<T extends LookupFilterableValue>(
  values: T[],
  lookupType: string,
  code: string,
  context: LookupFilterContext = {},
): T | undefined {
  return filterLookupValues(
    values.filter((value) => value.lookupType === lookupType),
    context,
  ).find((value) => value.code === code);
}

/**
 * @contract InspectionTypeOption
 * Purpose: Shared option shape produced for Inspection Type pickers in
 *   tenant-web and mobile-pwa.
 * Fields:  code, name, caseTypeCodes — the case-type linkage carried in the
 *   lookup's `config.caseTypeCodes` metadata.
 * Rules:   caseTypeCodes is always a string array (never undefined); non-string
 *   entries are dropped.
 * Sync:    derived from synced `inspectionType` lookup values; read-only.
 */
export interface InspectionTypeOption {
  code: string;
  name: string;
  caseTypeCodes: string[];
}

/**
 * @contract InspectionTypeOptionsContext
 * Purpose: Picker context shared by both hosts' listInspectionTypeOptions.
 * Fields:  entityCategory — restricts to types tagged for the entity kind;
 *          caseTypeCode — prefers types linked to the selected Case Type;
 *          activeModules — the tenant's enabled module codes (catalog codes
 *          are normalized to lookup module keys internally).
 * Rules:   absent activeModules = no module restriction.
 * Sync:    read-only; does not rewrite lookup documents.
 */
export interface InspectionTypeOptionsContext {
  entityCategory?: string | null;
  caseTypeCode?: string;
  activeModules?: Array<string | null | undefined>;
}

/** Values the shared inspection-type pipeline can read (both hosts' lookup row shapes qualify). */
export type InspectionTypeSourceValue = LookupFilterableValue & {
  config?: { caseTypeCodes?: unknown } | null;
};

/** Maps a lookup row to the base InspectionTypeOption (code/name/caseTypeCodes). */
export function toInspectionTypeOption(value: InspectionTypeSourceValue): InspectionTypeOption {
  return {
    code: value.code,
    name: value.name,
    caseTypeCodes: Array.isArray(value.config?.caseTypeCodes)
      ? value.config.caseTypeCodes.filter((code): code is string => typeof code === 'string')
      : [],
  };
}

/**
 * @process listInspectionTypeOptionsWithReader
 *
 * Steps:
 * 1. Read `inspectionType` lookup rows through the injected reader, filtered
 *    by entity category and the tenant's enabled modules. `activeModules`
 *    carries catalog codes — they are resolved to lookup module keys via
 *    `resolveLookupInspectionModulesForCatalog` (undefined stays
 *    unrestricted, an explicit empty set stays empty).
 * 2. Map each row to an option (base shape, or a host-specific extension via
 *    `mapOption`).
 * 3. Prefer types linked to the selected Case Type without emptying the list.
 *
 * Inputs:
 * - readInspectionTypes — host-injected Dexie reader; receives the filter
 *   context and returns inspectionType rows.
 * - context — entityCategory / caseTypeCode / activeModules (catalog codes).
 * - mapOption — optional host extension (e.g. tenant-web adds scope fields).
 *
 * Outputs:
 * - Promise resolving to the option list.
 *
 * Failure Handling:
 * - Reader errors propagate to the caller (hosts already surface them).
 *
 * Idempotency:
 * - REQUIRED; read-only.
 */
export async function listInspectionTypeOptionsWithReader<
  TValue extends InspectionTypeSourceValue,
  TOption extends InspectionTypeOption = InspectionTypeOption,
>(
  readInspectionTypes: (context: LookupFilterContext) => Promise<TValue[]>,
  context: InspectionTypeOptionsContext = {},
  mapOption?: (value: TValue) => TOption,
): Promise<TOption[]> {
  const values = await readInspectionTypes({
    entityCategories: context.entityCategory ? [context.entityCategory] : undefined,
    inspectionModules: resolveLookupInspectionModulesForCatalog(context.activeModules),
  });
  const options = values.map((value): TOption =>
    mapOption ? mapOption(value) : (toInspectionTypeOption(value) as TOption));
  return preferInspectionTypesForCase(options, context.caseTypeCode);
}
